import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verificationAPI } from '../api/verificationAPI';

const ExpertVerificationPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'guide' | 'form' | 'pending' | 'done'>('guide');

  // 폼 상태 (2026-09-01: 문서확인번호 텍스트 입력 → 서류 파일 업로드 방식으로 전환)
  const [file, setFile] = useState<File | null>(null);
  const [insurerName, setInsurerName] = useState('');
  const [insuranceType, setInsuranceType] = useState('건강보험');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [isCurrent, setIsCurrent] = useState(false);
  const [consentPersonalInfo, setConsentPersonalInfo] = useState(false);

  useEffect(() => {
    console.log('[ExpertVerification] 페이지 로드, 상태 조회 시작');
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await verificationAPI.getStatus();
      console.log('[ExpertVerification] 상태 조회 성공:', res.data);
      setStatus(res.data);
      if (res.data?.state === 'verified') setStep('done');
      else if (res.data?.state === 'pending') setStep('pending');
    } catch (err) {
      console.error('[ExpertVerification] 상태 조회 실패:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSubmit = async () => {
    console.log('[ExpertVerification] 서류 제출 시작');
    setError('');

    if (!file) {
      setError(t('verify.errDocument'));
      return;
    }
    if (!insurerName.trim()) {
      setError(t('verify.errInsurer'));
      return;
    }
    if (!periodStart) {
      setError(t('verify.errStart'));
      return;
    }
    if (!isCurrent && !periodEnd) {
      setError(t('verify.errEnd'));
      return;
    }
    if (!consentPersonalInfo) {
      setError(t('verify.errConsent'));
      return;
    }

    setLoading(true);
    try {
      const res = await verificationAPI.uploadDocument(
        file,
        insurerName.trim(),
        insuranceType,
        periodStart,
        isCurrent ? null : periodEnd,
        isCurrent,
        consentPersonalInfo
      );
      console.log('[ExpertVerification] 제출 성공:', res);
      await loadStatus();
      setStep('pending');
    } catch (err: any) {
      console.error('[ExpertVerification] 제출 실패:', err);
      setError(err.response?.data?.message || t('verify.errProcess'));
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = () => {
    console.log('[ExpertVerification] 재제출 시작');
    setFile(null);
    setInsurerName('');
    setPeriodStart('');
    setPeriodEnd('');
    setIsCurrent(false);
    setConsentPersonalInfo(false);
    setError('');
    setStep('guide');
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US');
  };

  const getDaysLeftColor = (days: number) => {
    if (days <= 7) return '#dc3545';
    if (days <= 30) return '#ffc107';
    return '#28a745';
  };

  if (statusLoading) {
    return (
      <div style={{ maxWidth: 600, margin: '60px auto', padding: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 16 }}>⏳</div>
        <p style={{ color: '#666' }}>{t('verify.checkingStatus')}</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 620, margin: '40px auto', padding: '20px' }}>
      <h1 style={{ marginBottom: 6, fontSize: 24 }}>🔐 {t('verify.title')}</h1>
      <p style={{ color: '#666', marginBottom: 28, fontSize: 14 }}>
        {t('verify.subtitle')}
      </p>

      {/* ── 검토 중 상태 ── */}
      {step === 'pending' && status?.state === 'pending' && (
        <div style={{ marginBottom: 28, padding: 24, background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 12 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🕓</div>
          <h3 style={{ margin: '0 0 8px', color: '#8a6d00' }}>{t('verify.pendingTitle')}</h3>
          <p style={{ margin: 0, fontSize: 14, color: '#555', lineHeight: 1.6 }}>{t('verify.pendingDesc')}</p>
        </div>
      )}

      {/* ── 반려됨 ── */}
      {status?.state === 'rejected' && step !== 'form' && (
        <div style={{ marginBottom: 20, padding: 16, background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 10, fontSize: 14, color: '#721c24' }}>
          <div>❌ {t('verify.rejectedMsg', { reason: status.rejectReason || '-' })}</div>
          <button
            onClick={() => { console.log('[ExpertVerification] 반려 후 재제출 클릭'); setStep('guide'); }}
            style={{ marginTop: 10, padding: '8px 14px', background: '#fff', border: '2px solid #dc3545', borderRadius: 8, color: '#dc3545', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
          >
            {t('verify.reuploadBtn')}
          </button>
        </div>
      )}

      {/* ── 인증 완료 상태 ── */}
      {step === 'done' && status?.state === 'verified' && (
        <div style={{ marginBottom: 28, padding: 24, background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 12 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
          <h3 style={{ margin: '0 0 12px', color: '#155724' }}>{t('report.verified')}</h3>
          <div style={{ fontSize: 14, color: '#333', lineHeight: 1.8 }}>
            <div><b>{t('verify.insurer')}:</b> {status.history?.[0]?.insurer_name || '-'}</div>
            <div><b>{t('verify.insType')}:</b> {status.history?.[0]?.insurance_type || '-'}</div>
            <div><b>{t('verify.verifiedAt')}:</b> {formatDate(status.verifiedAt)}</div>
            <div>
              <b>{t('verify.expiresAt')}:</b> {formatDate(status.expiresAt)}&nbsp;
              <span style={{ color: getDaysLeftColor(status.daysLeft), fontWeight: 600 }}>
                {t('verify.daysLeft', { n: status.daysLeft })}
              </span>
            </div>
          </div>
          {status.daysLeft <= 30 && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, fontSize: 13 }}>
              ⚠️ {t('verify.expiryWarn', { n: status.daysLeft })}
            </div>
          )}
          <button
            onClick={handleRenew}
            style={{ marginTop: 18, padding: '10px 20px', background: '#fff', border: '2px solid #28a745', borderRadius: 8, color: '#28a745', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
          >
            🔄 {t('verify.renew')}
          </button>

          {/* 리포트 통합 안내 */}
          <div style={{ marginTop: 16, padding: '14px 16px', background: 'linear-gradient(135deg,#4F46E510,#7C3AED10)', border: '1px solid #a78bfa44', borderRadius: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED', marginBottom: 6 }}>
              🤖 {t('verify.reportNotice')}
            </div>
            <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6, marginBottom: 10 }}>
              {t('verify.reportNoticeDesc')}
            </div>
            {user && (
              <button
                onClick={() => navigate(`/report/${user.id}`)}
                style={{ padding: '8px 16px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('verify.previewReport')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* 만료된 경우 */}
      {status?.state === 'expired' && (
        <div style={{ marginBottom: 20, padding: 16, background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 10, fontSize: 14, color: '#721c24' }}>
          ⚠️ {t('verify.expiredMsg')}
        </div>
      )}

      {/* ── Step 1: 발급 안내 ── */}
      {step === 'guide' && (
        <>
          <div style={{ padding: 20, background: '#e7f3ff', border: '1px solid #b6d4fe', borderRadius: 12, marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 12px', color: '#0c4a8a' }}>📋 {t('verify.step1Title')}</h4>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: '#333' }}>
              {t('verify.step1Desc')}
            </p>
            <div style={{ background: '#fff', borderRadius: 8, padding: 16, fontSize: 13, lineHeight: 2 }}>
              <div>
                <b>🏥 {t('verify.nhis')}</b><br />
                <a href="https://www.nhis.or.kr" target="_blank" rel="noreferrer" style={{ color: '#0d6efd' }}>nhis.or.kr</a>
                &nbsp;→ {t('verify.nhisPath')}
              </div>
              <div style={{ marginTop: 12 }}>
                <b>🏛️ {t('verify.gov24')}</b><br />
                <a href="https://www.gov.kr" target="_blank" rel="noreferrer" style={{ color: '#0d6efd' }}>gov.kr</a>
                &nbsp;→ {t('verify.gov24Path')}
              </div>
              <div style={{ marginTop: 12 }}>
                <b>📱 {t('verify.app')}</b><br />
                {t('verify.appPath')}
              </div>
            </div>
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#fff3cd', borderRadius: 8, fontSize: 13 }}>
              💡 {t('verify.tip')}
            </div>
          </div>

          <button
            onClick={() => { console.log('[ExpertVerification] 다음 단계 클릭'); setStep('form'); }}
            style={{ width: '100%', padding: 16, background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}
          >
            {t('verify.goToForm')}
          </button>
        </>
      )}

      {/* ── Step 2: 번호 입력 폼 ── */}
      {step === 'form' && (
        <>
          <div style={{ padding: 20, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 12, marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 16px', color: '#333' }}>📝 {t('verify.step2Title')}</h4>

            {/* 서류 업로드 */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                {t('verify.uploadLabel')}
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={e => {
                  const f = e.target.files?.[0] || null;
                  console.log('[ExpertVerification] 서류 파일 선택:', f?.name);
                  setFile(f);
                }}
                style={{ width: '100%', padding: '10px 0', fontSize: 14 }}
              />
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{t('verify.uploadHint')}</div>
            </div>

            {/* 사업장명 */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                {t('verify.insurerLabel')}
              </label>
              <input
                type="text"
                value={insurerName}
                onChange={e => { console.log('[ExpertVerification] 사업장명 입력'); setInsurerName(e.target.value); }}
                placeholder={t('verify.insurerPlaceholder')}
                style={{ width: '100%', padding: '12px 14px', border: '2px solid #dee2e6', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }}
              />
            </div>

            {/* 보험 종류 */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                {t('verify.insTypeReq')}
              </label>
              <select
                value={insuranceType}
                onChange={e => { console.log('[ExpertVerification] 보험종류 선택:', e.target.value); setInsuranceType(e.target.value); }}
                style={{ width: '100%', padding: '12px 14px', border: '2px solid #dee2e6', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }}
              >
                <option value="건강보험">{t('verify.health')}</option>
                <option value="국민연금">{t('verify.pension')}</option>
                <option value="고용보험">{t('verify.employment')}</option>
                <option value="산재보험">{t('verify.accident')}</option>
              </select>
            </div>

            {/* 가입 기간 */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                {t('verify.period')}
              </label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="month"
                  value={periodStart}
                  onChange={e => { console.log('[ExpertVerification] 시작일 선택'); setPeriodStart(e.target.value); }}
                  style={{ flex: 1, minWidth: 140, padding: '12px 14px', border: '2px solid #dee2e6', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
                <span style={{ color: '#666' }}>~</span>
                <input
                  type="month"
                  value={periodEnd}
                  disabled={isCurrent}
                  onChange={e => { console.log('[ExpertVerification] 종료일 선택'); setPeriodEnd(e.target.value); }}
                  style={{ flex: 1, minWidth: 140, padding: '12px 14px', border: '2px solid #dee2e6', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: isCurrent ? '#f0f0f0' : '#fff' }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isCurrent}
                  onChange={e => { console.log('[ExpertVerification] 현재재직중 체크:', e.target.checked); setIsCurrent(e.target.checked); }}
                />
                {t('verify.current')}
              </label>
            </div>

            {/* 개인정보 동의 */}
            <div style={{ marginBottom: 16, padding: 14, background: '#fff', border: '1px solid #dee2e6', borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, cursor: 'pointer', lineHeight: 1.6 }}>
                <input
                  type="checkbox"
                  checked={consentPersonalInfo}
                  onChange={e => { console.log('[ExpertVerification] 개인정보 동의:', e.target.checked); setConsentPersonalInfo(e.target.checked); }}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <span>
                  <b>{t('verify.consentTitle')}</b><br />
                  {t('verify.consentItems')}<br />
                  {t('verify.consentPurpose')}<br />
                  {t('verify.consentRetention')}
                </span>
              </label>
            </div>

            {error && (
              <div style={{ marginBottom: 14, padding: '10px 14px', background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 8, color: '#dc3545', fontSize: 14 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { console.log('[ExpertVerification] 이전 클릭'); setStep('guide'); }}
                style={{ flex: 1, padding: 14, background: '#fff', border: '2px solid #dee2e6', borderRadius: 8, fontSize: 15, cursor: 'pointer', color: '#666' }}
              >
                ← {t('common.prev')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{ flex: 2, padding: 14, background: loading ? '#adb5bd' : '#0d6efd', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? t('verify.processing') : '📤 ' + t('verify.submit')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* 인증 이력 */}
      {status?.history?.length > 0 && (
        <div style={{ marginTop: 30 }}>
          <h4 style={{ marginBottom: 12, color: '#555', fontSize: 14 }}>📁 {t('verify.history')}</h4>
          {status.history.map((h: any, i: number) => (
            <div key={i} style={{ padding: '10px 14px', background: '#f8f9fa', borderRadius: 8, marginBottom: 8, fontSize: 13, color: '#555', display: 'flex', justifyContent: 'space-between' }}>
              <span>{h.insurer_name} ({h.insurance_type})</span>
              <span style={{ color: h.status === 'verified' ? '#28a745' : h.status === 'pending' ? '#8a6d00' : '#aaa' }}>
                {h.status === 'verified' ? '✅ ' + t('verify.valid')
                  : h.status === 'pending' ? '🕓 ' + t('verify.pendingShort')
                  : h.status === 'rejected' ? '❌ ' + t('verify.rejectedShort')
                  : t('verify.expiredShort')} · {formatDate(h.created_at)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExpertVerificationPage;
